import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { destinationCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { NotesIcon, NotifyCompanyIcon } from 'components/Shared/Icons/tooltip_icons';
import { Destination } from 'types/entities/destination_type';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditDestination from './AddOrEditDestination';
import NotifyCard from '../notifies/NotifyCard';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Destination;
  showActionButtons: boolean;
  onDestinationDelete?: () => void;
  onDestinationUpdate?: () => void;
}

const DestinationCard = (props: Props) => {
  const { option: destination, showActionButtons = true, onDestinationDelete, onDestinationUpdate } = props;

  const [editDestinationModalOpen, setEditDestinationModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteDestination = () => {
    axiosService
      .delete(`/entities/destination/${destination.id}/`)
      .then(() => {
        if (onDestinationDelete) {
          onDestinationDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onDestinationEdit = () => {
    if (onDestinationUpdate) {
      onDestinationUpdate();
    }
    setEditDestinationModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={destinationCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={destinationCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditDestinationModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={destinationCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={destination.name} tooltipText={destination.id} />}
          subheader="Destination"
          sx={destinationCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={destinationCardStyles.contactBox}>
            <NotesIcon />
            <Typography color="text" sx={destinationCardStyles.contactValue}>
              {destination.notes || '-'}
            </Typography>
          </Box>
          {destination.notifies.map(notify => (
            <Box key={notify.id} sx={destinationCardStyles.contactBox}>
              <NotifyCompanyIcon />
              <Tooltip
                title={<NotifyCard option={notify} showActionButtons={false} />}
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'transparent',
                      maxWidth: 'none',
                    },
                  },
                }}
              >
                <Typography color="text" sx={destinationCardStyles.contactValue}>
                  {notify.company.name}
                </Typography>
              </Tooltip>
            </Box>
          ))}
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete destination</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete destination <b>{destination.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteDestination} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditDestination
        destination={destination}
        onSave={onDestinationEdit}
        modalTitle="Edit destination"
        isModalOpen={editDestinationModalOpen}
        setModalOpen={setEditDestinationModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default DestinationCard;
